import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { IAdminShopVoucher, IVouchersImportXls } from 'types/income';
import { TableDataResponse } from 'types/general';
import { exportXlsxConfig, getFormData, getSorting } from 'utils/general';
import { RedeemMethods, VoucherStatus } from 'store/vouchers';
import { AdminShopVouchersFilters } from './admin-shop-vouchers.types';

interface GetShopVouchers {
  shopItemId: string;
  filters: Partial<AdminShopVouchersFilters>;
}

interface GetShopVoucher {
  voucherId: string;
}

interface EditShopVoucher {
  voucherId: string;
  data: Partial<
    {
      status: VoucherStatus;
      purchasePrice: number;
      purchaseReference: string;
      shopItemPriceId: string;
      shopOrderId: string;
      expiresAt: string | null;
    } & RedeemMethods
  >;
}

interface ExportVouchersXls {
  voucherIds: string[];
}

interface ImportVouchersXls {
  shopItemId: string;
  shopItemPriceId: string;
  file: File;
}

interface ImportVouchersFiles {
  shopItemId: string;
  shopItemPriceId: string;
  purchasePrice: number;
  purchaseReference: string;
  expiresAt: string | null;
  files: File[];
}

interface BulkUpdate {
  voucherIds: string[];
  data: Partial<{ status: VoucherStatus; expirationDate: string }>;
}

interface BulkDelete {
  voucherIds: string[];
}

export const loaders = {
  getShopVouchers: ({ shopItemId, filters }: GetShopVouchers) => {
    return api<TableDataResponse<IAdminShopVoucher>>({
      url: Endpoints.melpAdmin.shopVouchers.root.replace(
        ':shopItemId',
        shopItemId,
      ),
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        ...getSorting(filters.sort),
        search: filters.search || undefined,
        filter: {
          status: filters.status,
          marketplaceItemPriceId: filters.prices,
          creationDateFrom: filters.createdAt?.from,
          creationDateTo: filters.createdAt?.to,
        },
      },
    });
  },
  getShopVoucher: ({ voucherId }: GetShopVoucher) => {
    return api<IAdminShopVoucher>({
      url: Endpoints.melpAdmin.shopVouchers.byId.replace(
        ':voucherId',
        voucherId,
      ),
    });
  },
  updateShopVoucher: ({ voucherId, data }: EditShopVoucher) => {
    return api({
      url: Endpoints.melpAdmin.shopVouchers.byId.replace(
        ':voucherId',
        voucherId,
      ),
      method: 'patch',
      data: {
        status: data.status,
        qrCode: data.qrCode,
        barCode: data.barCode,
        serial: data.serial,
        url: data.url,
        code: data.code,
        pin: data.pin,
        purchasePrice: data.purchasePrice,
        purchaseReference: data.purchaseReference,
        marketplaceItemPriceId: data.shopItemPriceId,
        marketplaceOrderId: data.shopOrderId,
        expirationDate: data.expiresAt,
      },
    });
  },
  bulkUpdate: ({ voucherIds, data }: BulkUpdate) => {
    return api({
      url: Endpoints.melpAdmin.shopVouchers.bulk.update,
      method: 'patch',
      data: {
        voucherIds,
        ...data,
      },
    });
  },
  bulkDelete: ({ voucherIds }: BulkDelete) => {
    return api({
      url: Endpoints.melpAdmin.shopVouchers.bulk.delete,
      method: 'delete',
      data: {
        ids: voucherIds,
      },
    });
  },
  exportXlsTemplate: () => {
    return api({
      url: Endpoints.melpAdmin.shopVouchers.import.xls,
      ...exportXlsxConfig,
    });
  },
  exportVouchersXls: ({ voucherIds }: ExportVouchersXls) => {
    return api({
      url: Endpoints.melpAdmin.shopVouchers.export.xls,
      params: {
        voucherIds,
      },
      ...exportXlsxConfig,
    });
  },
  importVouchersXls: ({
    shopItemId,
    shopItemPriceId,
    file,
  }: ImportVouchersXls) => {
    return api<IVouchersImportXls>({
      url: Endpoints.melpAdmin.shopVouchers.import.xls,
      method: 'post',
      data: getFormData([
        { field: 'file', value: file },
        { field: 'marketplaceItemId', value: shopItemId },
        { field: 'marketplaceItemPriceId', value: shopItemPriceId },
      ]),
    });
  },
  importVouchersFiles: ({
    shopItemId,
    shopItemPriceId,
    purchasePrice,
    purchaseReference,
    expiresAt,
    files,
  }: ImportVouchersFiles) => {
    return api({
      url: Endpoints.melpAdmin.shopVouchers.import.files,
      method: 'post',
      data: getFormData([
        ...files.map((file) => ({ field: 'files', value: file })),
        { field: 'marketplaceItemId', value: shopItemId },
        { field: 'marketplaceItemPriceId', value: shopItemPriceId },
        { field: 'purchaseValue', value: String(purchasePrice) },
        { field: 'purchaseReference', value: purchaseReference },
        ...(expiresAt ? [{ field: 'expirationDate', value: expiresAt }] : []),
      ]),
    });
  },
};
